import { Box, Button, Sheet, Tooltip, Typography } from "@mui/joy";
import { ReactNode } from "react";
import {
  MonthBucket,
  OrgStat,
  OTHER_ORG_COLOR,
  TypeStat,
  VenueStat,
} from "./useInsights";

export const cardSx = {
  borderRadius: "18px",
  border: "1px solid rgba(120, 120, 128, 0.14)",
  background: "var(--mui-palette-background-surface, #fff)",
  boxShadow: "0 1px 2px rgba(0,0,0,0.04), 0 8px 24px rgba(0,0,0,0.04)",
} as const;

/**
 * Formats a duration in hours into a compact, human label.
 *
 * @param hours raw hours
 * @returns {string} e.g. "0h", "6.5h", "128h"
 */
export function formatHours(hours: number): string {
  if (!hours) return "0h";
  if (hours < 10) return `${Math.round(hours * 10) / 10}h`;
  return `${Math.round(hours)}h`;
}

/**
 * Big headline metric tile, iOS-settings style with a colored icon chip.
 *
 * @param root0 props
 * @param root0.icon FontAwesome class
 * @param root0.color icon chip background
 * @param root0.value the metric value
 * @param root0.label caption beneath the value
 * @param root0.hint optional secondary line
 * @returns {ReactElement} StatCard
 */
export function StatCard({
  icon,
  color,
  value,
  label,
  hint,
}: {
  icon: string;
  color: string;
  value: ReactNode;
  label: string;
  hint?: string;
}) {
  return (
    <Sheet
      sx={{
        ...cardSx,
        p: 2,
        display: "flex",
        flexDirection: "column",
        gap: 0.75,
        flex: 1,
        minWidth: 0,
      }}
    >
      <Box
        sx={{
          width: 30,
          height: 30,
          borderRadius: "9px",
          background: color,
          color: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 14,
        }}
      >
        <i className={icon}></i>
      </Box>
      <Typography
        sx={{
          fontSize: "1.75rem",
          fontWeight: 700,
          letterSpacing: "-0.02em",
          lineHeight: 1.1,
          mt: 0.5,
        }}
      >
        {value}
      </Typography>
      <Typography
        level="body3"
        sx={{ color: "var(--mui-palette-text-secondary)", fontWeight: 600 }}
      >
        {label}
      </Typography>
      {hint ? (
        <Typography
          level="body4"
          sx={{ color: "var(--mui-palette-text-tertiary)" }}
        >
          {hint}
        </Typography>
      ) : null}
    </Sheet>
  );
}

/**
 * Titled content card with an optional colored icon and right-aligned action.
 *
 * @param root0 props
 * @param root0.icon optional FontAwesome class
 * @param root0.iconColor optional icon chip background
 * @param root0.title section title
 * @param root0.subtitle optional caption under the title
 * @param root0.action optional right-aligned node (e.g. a toggle)
 * @param root0.children section body
 * @returns {ReactElement} SectionCard
 */
export function SectionCard({
  icon,
  iconColor = "#8E8E93",
  title,
  subtitle,
  action,
  children,
}: {
  icon?: string;
  iconColor?: string;
  title: string;
  subtitle?: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <Sheet
      sx={{
        ...cardSx,
        p: 2.5,
        display: "flex",
        flexDirection: "column",
        gap: 2,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.25 }}>
        {icon ? (
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: "8px",
              background: iconColor,
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 13,
              flexShrink: 0,
            }}
          >
            <i className={icon}></i>
          </Box>
        ) : null}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: "1.0625rem",
              letterSpacing: "-0.01em",
            }}
          >
            {title}
          </Typography>
          {subtitle ? (
            <Typography
              level="body3"
              sx={{ color: "var(--mui-palette-text-secondary)" }}
            >
              {subtitle}
            </Typography>
          ) : null}
        </Box>
        {action ? <Box sx={{ flexShrink: 0 }}>{action}</Box> : null}
      </Box>
      {children}
    </Sheet>
  );
}

/**
 * Monthly activity bar chart, drawn with plain boxes (no chart dependency).
 * Each bar is stacked by organization using the same per-org colors as the
 * leaderboard.
 *
 * @param root0 props
 * @param root0.months ordered month buckets
 * @param root0.organizations in-range organizations, globally ranked (for legend order)
 * @param root0.onSelectMonth
 * @returns {ReactElement} ActivityChart
 */
export function ActivityChart({
  months,
  organizations,
  onSelectMonth,
}: {
  months: MonthBucket[];
  organizations: OrgStat[];
  onSelectMonth?: (month: MonthBucket) => void;
}) {
  const max = Math.max(1, ...months.map((m) => m.count));
  // Keep labels readable: thin out when there are many months.
  const step = months.length > 18 ? 6 : months.length > 12 ? 3 : 1;
  const gap = months.length > 24 ? "2px" : "4px";

  // Show the year on the first labeled bucket and whenever the year changes
  // among labeled buckets — independent of which month gets a label — so a
  // multi-year axis is never ambiguous.
  let lastLabeledYear: number | undefined;
  const labelInfo = months.map((m, i) => {
    const labeled = i % step === 0 || i === months.length - 1;
    let showYear = false;
    if (labeled) {
      showYear = lastLabeledYear === undefined || m.year !== lastLabeledYear;
      lastLabeledYear = m.year;
    }
    return { labeled, showYear };
  });

  // Legend ordered by the same global organization rank as the leaderboard and
  // the stacking order, with the "Other" bucket last — so the three charts
  // read consistently. Only organizations that actually appear are shown.
  const present = new Set<number>();
  months.forEach((m) => m.segments.forEach((s) => present.add(s.id)));
  const legend: { id: number; name: string; color: string }[] = organizations
    .filter((o) => present.has(o.id) && o.color !== OTHER_ORG_COLOR)
    .map((o) => ({ id: o.id, name: o.name, color: o.color }));
  if (present.has(0)) {
    legend.push({ id: 0, name: "Other", color: OTHER_ORG_COLOR });
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
      <Box sx={{ display: "flex", alignItems: "flex-end", gap, height: 132 }}>
        {months.map((m) => {
          const h = m.count === 0 ? 3 : 8 + (m.count / max) * 116;
          const period = `${m.label} ${m.year}`;
          if (m.count === 0) {
            return (
              <Tooltip
                key={m.key}
                size="sm"
                arrow
                variant="solid"
                title={`${period} — no services`}
              >
                <Box
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    height: h,
                    borderRadius: "5px 5px 3px 3px",
                    background: "rgba(120,120,128,0.16)",
                  }}
                />
              </Tooltip>
            );
          }
          return (
            <Box
              key={m.key}
              onClick={onSelectMonth ? () => onSelectMonth(m) : undefined}
              sx={{
                flex: 1,
                minWidth: 0,
                height: h,
                borderRadius: "5px 5px 3px 3px",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column-reverse",
                transition: "opacity 0.15s ease",
                cursor: onSelectMonth ? "pointer" : "default",
                "&:hover": { opacity: 0.85 },
              }}
            >
              {m.segments.map((s) => (
                <Tooltip
                  key={s.id}
                  size="sm"
                  arrow
                  variant="solid"
                  title={`${s.name} — ${s.count} service${
                    s.count === 1 ? "" : "s"
                  }${s.hours ? ` · ${formatHours(s.hours)}` : ""} · ${period}`}
                >
                  <Box
                    sx={{
                      flexGrow: s.count,
                      flexBasis: 0,
                      minHeight: 2,
                      background: s.color,
                      cursor: "default",
                    }}
                  />
                </Tooltip>
              ))}
            </Box>
          );
        })}
      </Box>
      <Box sx={{ display: "flex", gap }}>
        {months.map((m, i) => (
          <Box
            key={m.key}
            sx={{
              flex: 1,
              minWidth: 0,
              textAlign: "center",
              overflow: "hidden",
            }}
          >
            {labelInfo[i].labeled ? (
              <Typography
                level="body4"
                sx={{
                  color: "var(--mui-palette-text-tertiary)",
                  fontSize: "0.625rem",
                  whiteSpace: "nowrap",
                }}
              >
                {m.label}
                {labelInfo[i].showYear ? ` ’${`${m.year}`.slice(2)}` : ""}
              </Typography>
            ) : null}
          </Box>
        ))}
      </Box>
      {legend.length ? (
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 1,
            mt: 0.5,
            pt: 1,
            borderTop: "1px solid rgba(120, 120, 128, 0.14)",
          }}
        >
          {legend.map((l) => (
            <Box
              key={l.id}
              sx={{ display: "flex", alignItems: "center", gap: 0.625 }}
            >
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: "3px",
                  background: l.color,
                  flexShrink: 0,
                }}
              />
              <Typography
                level="body4"
                sx={{ color: "var(--mui-palette-text-secondary)" }}
                noWrap
              >
                {l.name}
              </Typography>
            </Box>
          ))}
        </Box>
      ) : null}
    </Box>
  );
}

/**
 * A single horizontal bar row — rank, label, proportional bar and value.
 *
 * @param root0 props
 * @param root0.rank optional 1-based rank shown as a chip
 * @param root0.icon optional FontAwesome class drawn instead of a rank chip
 * @param root0.label the row label
 * @param root0.sublabel optional muted text shown after the label
 * @param root0.ratio bar fill ratio in [0, 1]
 * @param root0.primary bold trailing value
 * @param root0.secondary muted trailing value
 * @param root0.color bar color
 * @param root0.rankColor
 * @param root0.onClick optional click handler — makes the row interactive
 * @returns {ReactElement} BarRow
 */
function BarRow({
  rank,
  rankColor,
  icon,
  label,
  sublabel,
  ratio,
  primary,
  secondary,
  color = "#0A84FF",
  onClick,
}: {
  rank?: number;
  rankColor?: string;
  icon?: string;
  label: string;
  sublabel?: string;
  ratio: number;
  primary: string;
  secondary?: string;
  color?: string;
  onClick?: () => void;
}) {
  return (
    <Box
      onClick={onClick}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.25,
        ...(onClick
          ? {
              cursor: "pointer",
              mx: -1,
              px: 1,
              py: 0.5,
              borderRadius: "10px",
              transition: "background 0.15s ease",
              "&:hover": { background: "rgba(120, 120, 128, 0.1)" },
            }
          : {}),
      }}
    >
      {rank !== undefined ? (
        <Box
          sx={{
            width: 22,
            height: 22,
            borderRadius: "7px",
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "0.75rem",
            fontWeight: 700,
            color: rankColor ? "#fff" : "var(--mui-palette-text-secondary)",
            background: rankColor ?? "rgba(120, 120, 128, 0.14)",
          }}
        >
          {rank}
        </Box>
      ) : null}
      {icon ? (
        <Box
          sx={{
            width: 22,
            textAlign: "center",
            flexShrink: 0,
            color: "var(--mui-palette-text-secondary)",
            fontSize: 13,
          }}
        >
          <i className={icon}></i>
        </Box>
      ) : null}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            gap: 1,
            mb: 0.5,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "baseline",
              gap: 0.75,
              minWidth: 0,
            }}
          >
            <Typography level="body2" sx={{ fontWeight: 600 }} noWrap>
              {label}
            </Typography>
            {sublabel ? (
              <Typography
                level="body4"
                sx={{
                  color: "var(--mui-palette-text-tertiary)",
                  flexShrink: 0,
                }}
                noWrap
              >
                {sublabel}
              </Typography>
            ) : null}
          </Box>
          <Typography level="body2" sx={{ flexShrink: 0, fontWeight: 700 }}>
            {primary}
            {secondary ? (
              <Typography
                component="span"
                level="body3"
                sx={{
                  color: "var(--mui-palette-text-tertiary)",
                  fontWeight: 500,
                  ml: 0.5,
                }}
              >
                · {secondary}
              </Typography>
            ) : null}
          </Typography>
        </Box>
        <Box
          sx={{
            height: 7,
            borderRadius: "999px",
            background: "rgba(120, 120, 128, 0.14)",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              height: "100%",
              width: `${Math.max(ratio * 100, ratio > 0 ? 4 : 0)}%`,
              borderRadius: "999px",
              background: color,
              transition: "width 0.4s cubic-bezier(0.3, 0, 0.2, 1)",
            }}
          />
        </Box>
      </Box>
    </Box>
  );
}

/**
 * Organization leaderboard — who books the musician the most, with services
 * or hours as the ranking metric.
 *
 * @param root0 props
 * @param root0.organizations org stats, pre-sorted
 * @param root0.metric the active ranking metric
 * @param root0.limit max rows to render
 * @param root0.onSelect optional row click handler
 * @returns {ReactElement} Leaderboard
 */
export function Leaderboard({
  organizations,
  metric,
  limit = 8,
  onSelect,
}: {
  organizations: OrgStat[];
  metric: "services" | "hours";
  limit?: number;
  onSelect?: (org: OrgStat) => void;
}) {
  const rows = organizations.slice(0, limit);
  const max = Math.max(
    1,
    ...rows.map((o) => (metric === "services" ? o.count : o.hours))
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.75 }}>
      {rows.map((o, i) => {
        const value = metric === "services" ? o.count : o.hours;
        return (
          <BarRow
            key={o.id}
            rank={i + 1}
            rankColor={o.color}
            color={o.color}
            label={o.name}
            ratio={value / max}
            primary={
              metric === "services" ? `${o.count}` : formatHours(o.hours)
            }
            secondary={
              metric === "services"
                ? formatHours(o.hours)
                : `${o.count} service${o.count === 1 ? "" : "s"}`
            }
            onClick={onSelect ? () => onSelect(o) : undefined}
          />
        );
      })}
    </Box>
  );
}

/**
 * Top venues — where the musician performs most, ranked by services with
 * hours alongside. A single venue accent color keeps it visually distinct
 * from the multi-colored organization leaderboard.
 *
 * @param root0 props
 * @param root0.venues venue stats, pre-sorted by services
 * @param root0.limit max rows to render
 * @param root0.onSelect optional row click handler
 * @returns {ReactElement} VenueList
 */
export function VenueList({
  venues,
  limit = 6,
  onSelect,
}: {
  venues: VenueStat[];
  limit?: number;
  onSelect?: (venue: VenueStat) => void;
}) {
  const rows = venues.slice(0, limit);
  const max = Math.max(1, ...rows.map((v) => v.count));

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.75 }}>
      {rows.map((v, i) => (
        <BarRow
          key={v.id}
          rank={i + 1}
          color="#FF2D55"
          label={v.name}
          sublabel={v.location || undefined}
          ratio={v.count / max}
          primary={`${v.count}`}
          secondary={formatHours(v.hours)}
          onClick={onSelect ? () => onSelect(v) : undefined}
        />
      ))}
    </Box>
  );
}

/**
 * Session-type mix — how the musician's services break down by type.
 *
 * @param root0 props
 * @param root0.types type stats, pre-sorted
 * @param root0.onSelect optional row click handler
 * @returns {ReactElement} TypeBreakdown
 */
export function TypeBreakdown({
  types,
  onSelect,
}: {
  types: TypeStat[];
  onSelect?: (type: TypeStat) => void;
}) {
  const total = types.reduce((s, t) => s + t.count, 0);
  const max = Math.max(1, ...types.map((t) => t.count));
  const palette: Record<number, string> = {
    2: "#AF52DE", // Performance
    1: "#FF9500", // Recording Session
    13: "#0A84FF", // Rehearsal
    14: "#5856D6", // Dress Rehearsal
    15: "#5AC8FA", // Sound Check
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.75 }}>
      {types.map((t) => (
        <BarRow
          key={t.sessionTypeID}
          icon={t.icon}
          label={t.title}
          ratio={t.count / max}
          color={palette[t.sessionTypeID] ?? "#0A84FF"}
          primary={`${t.count}`}
          secondary={
            total ? `${Math.round((t.count / total) * 100)}%` : undefined
          }
          onClick={onSelect ? () => onSelect(t) : undefined}
        />
      ))}
    </Box>
  );
}

/**
 * Frosted lock overlay shown over blurred placeholder insights for non-Pro
 * members, with a call to action to upgrade.
 *
 * @param root0 props
 * @param root0.onUnlock invoked when the upgrade button is pressed
 * @returns {ReactElement} ProLock
 */
export function ProLock({ onUnlock }: { onUnlock: () => void }) {
  return (
    <Box
      sx={{
        position: "absolute",
        inset: 0,
        zIndex: 5,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
      }}
    >
      <Sheet
        sx={{
          maxWidth: 380,
          textAlign: "center",
          p: 4,
          borderRadius: "22px",
          background: "var(--mui-palette-background-surface, #fff)",
          boxShadow:
            "0 12px 24px rgba(0,0,0,0.12), 0 32px 80px rgba(0,0,0,0.24)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 1,
        }}
      >
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: "16px",
            background: "linear-gradient(135deg, #5856D6 0%, #AF52DE 100%)",
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 24,
            mb: 0.5,
          }}
        >
          <i className="fa-solid fa-chart-line"></i>
        </Box>
        <Typography
          level="h5"
          sx={{ fontWeight: 700, letterSpacing: "-0.01em" }}
        >
          Unlock your Insights
        </Typography>
        <Typography
          level="body2"
          sx={{ color: "var(--mui-palette-text-secondary)" }}
        >
          See how many services you've played, who books you the most, and the
          hours you've put in — with Rhapsody Pro.
        </Typography>
        <Button
          onClick={onUnlock}
          size="lg"
          color="info"
          startDecorator={<i className="fa-solid fa-sparkles"></i>}
          sx={{ borderRadius: "999px", fontWeight: 700, px: 3, mt: 1 }}
        >
          Get Rhapsody Pro
        </Button>
      </Sheet>
    </Box>
  );
}
